import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import RecentReviewProductsCarousel from "../RecentReviewProductsCarousel/RecentReviewProductsCarousel";

function ShopByCategory({ publicCategory, designerDiscount, products }) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const navigate = useNavigate();


  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -400,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 400,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="pt-8 bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4">
        {publicCategory?.categories?.response?.length > 0 &&
          (
            <>
              <div className="flex items-end justify-between mb-6">
                <h2 className="section-heading text-[28px] md:text-[32px] leading-9">
                  Shop By Category
                </h2>
              </div>

              <div className="relative px-1 sm:px-2 md:px-0">
                {canScrollLeft && (
                  <button
                    onClick={scrollLeft}
                    className="absolute left-0 top-[calc(50%-1.25rem)] -translate-y-1/2 -translate-x-2 sm:-translate-x-3 z-10 bg-white border border-stone-200 rounded-full shadow-md p-2 text-stone-800 hover:bg-stone-900 hover:text-white transition-colors"
                    aria-label="Scroll categories left"
                  >
                    <ChevronLeft size={20} />
                  </button>
                )}

                {canScrollRight && (
                  <button
                    onClick={scrollRight}
                    className="absolute right-0 top-[calc(50%-1.25rem)] -translate-y-1/2 translate-x-2 sm:translate-x-3 z-10 bg-white border border-stone-200 rounded-full shadow-md p-2 text-stone-800 hover:bg-stone-900 hover:text-white transition-colors"
                    aria-label="Scroll categories right"
                  >
                    <ChevronRight size={20} />
                  </button>
                )}

                <div
                  ref={scrollContainerRef}
                  className="flex md:grid md:grid-cols-5 gap-4 overflow-x-auto md:overflow-visible pb-2 scroll-smooth"
                  style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                  }}
                  onScroll={updateScrollButtons}
                >
                  {publicCategory?.categories?.response?.map((category) => (
                    <Link
                      key={category.id}
                      to={`/${encodeURIComponent(category.name)}`}
                      className="group min-w-[42%] sm:min-w-[30%] md:min-w-0 focus:outline-none"
                    >
                      <article className="flex flex-col">
                        <div className="relative aspect-square overflow-hidden rounded-2xl bg-[#f4f1ee]">
                          {category.image ? (
                            <img
                              src={category.image}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.style.display = "none";
                                const placeholder = target.nextElementSibling;
                                if (placeholder) placeholder.style.display = "flex";
                              }}
                            />
                          ) : null}
                          <div
                            className={`absolute inset-0 items-center justify-center text-stone-400 ${category.image ? "hidden" : "flex"}`}
                          >
                            <span className="text-sm">{category.name}</span>
                          </div>
                        </div>
                        <h3 className="mt-3 text-center font-sans text-[15px] font-medium text-stone-800 tracking-wide">
                          {category.name}
                        </h3>
                      </article>
                    </Link>
                  ))}
                </div>
              </div>

            </>
          )}

        {/* recent review products  */}
        <section className="mt-3">
          <RecentReviewProductsCarousel products={products || []} />
        </section>

        {/* Designers on Discount Section */}
        {designerDiscount?.designerDiscountBanners?.response
          ?.slice(0, 4)
          ?.filter((banner) => {
            const today = new Date();
            const start = new Date(banner.startDate);
            const end = new Date(banner.endDate);

            return today >= start && today <= end; // ✅ valid date range
          })?.length > 0 && (
            <div className="mt-10 rounded-[28px] bg-cream px-6 py-10 sm:px-10 md:px-12 relative">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
                <div className="lg:col-span-1">
                  <h2 className="section-heading text-[28px] md:text-[32px] leading-9">
                    Featured<br className="hidden lg:block" /> Designers
                  </h2>
                  <p className="mt-3 text-stone-500 text-[14px] leading-relaxed">
                    Your celebration, beautifully curated
                  </p>
                  <a
                    href="/collections/designer-discount-banners"
                    className="inline-block mt-7 border border-stone-800 text-stone-800 font-sans text-[13px] px-7 py-2.5 hover:bg-stone-800 hover:text-white transition-colors"
                  >
                    Explore More
                  </a>
                </div>
              <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-5">
                {designerDiscount?.designerDiscountBanners?.response
                  ?.slice(0, 4)
                  ?.filter((banner) => {
                    const today = new Date();
                    const start = new Date(banner.startDate);
                    const end = new Date(banner.endDate);
                    return today >= start && today <= end; // ✅ only active banners
                  })
                  .map((banner) => (
                    <div
                      key={banner.id}
                      className="bg-transparent cursor-pointer group text-center"
                      onClick={() =>
                        navigate(
                          `/collections/vendor/${banner?.vendor?.name}/discount/${banner?.discountPercentage}`
                        )
                      }
                    >
                      <div className="bg-white p-2 shadow-[0_1px_8px_rgba(0,0,0,0.04)]">
                        <div className="relative aspect-[3/4] overflow-hidden bg-stone-100">
                          <img
                            src={banner.imageUrl}
                            alt={banner?.vendor?.name || "Designer Banner"}
                            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                            loading="lazy"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        </div>
                      </div>
                      <h3 className="mt-3 font-sans text-[14px] font-medium text-stone-800 truncate">
                        {banner?.vendor?.name}
                      </h3>
                      <p className="text-[11px] tracking-[0.18em] uppercase text-stone-500 mt-1 font-sans">
                        Shop now
                      </p>
                    </div>
                  ))}
              </div>
              </div>
            </div>
          )}
        <style jsx>{`
          .overflow-x-auto::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </section >

  );
}

export default ShopByCategory;
